// ============================================================
// Dashboard Page (Role Router)
// ============================================================
// Orchestrates the dashboard views based on the user's role.

import { Box, Paper, Typography, Chip } from '@mui/material';
import { Navigate } from 'react-router-dom';
import { ShieldAlert, Building2, Sparkles, HelpCircle } from 'lucide-react';
import { useMemo } from 'react';
import { tokens } from '@/theme/tokens';
import useAuthStore from '@/store/authStore';
import { Loader } from '@/components/feedback/Loader';
import { ErrorState } from '@/components/feedback/ErrorState';
import { useAuth } from '@/hooks/useAuth';
import { useDashboard } from '@/hooks/useDashboard';
import { useDashboardStats } from '@/hooks/useDashboardStats';
import { useDashboardCharts } from '@/hooks/useDashboardCharts';
import { useStudents } from '@/hooks/useStudents';
import { useTeachers } from '@/hooks/useTeachers';
import { ROLES } from '@/constants/roles';
// Role Dashboards
import { OwnerDashboard } from './roles/OwnerDashboard';
import { PrincipalDashboard } from './roles/PrincipalDashboard';
import { ReceptionDashboard } from './roles/ReceptionDashboard';
import { AccountantDashboard } from './roles/AccountantDashboard';
import { TeacherDashboard } from './roles/TeacherDashboard';

import { useSchool } from '@/hooks/useSchool';
import { isValidModule } from '@/utils/dynamicNavigation';

export function DashboardPage() {
  const { user } = useAuth();
  const { currentSchool, schools } = useSchool();
  const modules = useAuthStore((s) => s.modules);

  const normalizedRole = String(user?.role || '').toUpperCase();
  const isSuperAdmin = normalizedRole === 'SUPER_ADMIN';

  const userMods = Array.isArray(modules) ? modules.filter(isValidModule) : [];
  const schoolMods = currentSchool?.school_modules || currentSchool?.modules || [];
  const mappedSchoolMods = Array.isArray(schoolMods)
    ? schoolMods.map((sm) => sm.module || sm).filter(isValidModule)
    : [];
  const activeMods = userMods.length > 0 ? userMods : mappedSchoolMods;

  const hasNoAssignment = !isSuperAdmin && (!currentSchool || activeMods.length === 0);
  const enabled = !hasNoAssignment;

  // Call all hooks unconditionally at top level with enabled flag
  const { loading: dashboardLoading, error } = useDashboard({ enabled });
  const { students, loading: studentsLoading } = useStudents(1, 20, {}, { enabled });
  const { teachers, loading: teachersLoading } = useTeachers({ enabled });
  const { stats, activities } = useDashboardStats();
  const { charts } = useDashboardCharts();

  const loading = dashboardLoading || studentsLoading || teachersLoading;

  const combinedStats = useMemo(() => {
    if (!stats) return stats;

    const totalStudents = students ? students.length : 0;
    const activeStudents = students ? students.filter(s => s.status === 'active').length : 0;
    const newAdmissions = students ? students.filter(s => s.admissionType === 'New').length : 0;

    const totalTeachers = teachers ? teachers.length : 0;
    const activeTeachers = teachers ? teachers.filter(t => t.status === 'active').length : 0;

    if (totalStudents === 0) {
      return {
        ...stats,
        totalStudents: 0,
        presentToday: 0,
        absentToday: 0,
        newAdmissions: 0,
        monthlyCollection: 0,
        pendingFees: 0,
        todaysCollection: 0,
        todaysReceipts: 0,
        studentRequests: 0,
        todaysAdmissions: 0,
        totalTeachers,
        activeTeachers,
      };
    }

    return {
      ...stats,
      totalStudents,
      presentToday: activeStudents,
      absentToday: totalStudents - activeStudents,
      newAdmissions,
      totalTeachers,
      activeTeachers,
    };
  }, [stats, students, teachers]);

  const combinedCharts = useMemo(() => {
    if (!charts) return charts;
    if (students && students.length === 0) {
      return {
        monthlyRevenue: charts.monthlyRevenue?.map(c => ({ ...c, revenue: 0 })),
        attendanceOverview: charts.attendanceOverview?.map(c => ({ ...c, present: 0, absent: 0 })),
        studentGrowth: charts.studentGrowth?.map(c => ({ ...c, students: 0 })),
      };
    }
    return charts;
  }, [charts, students]);

  const combinedActivities = useMemo(() => {
    if (!activities) return [];
    if (students && students.length === 0) return [];
    return activities.filter(a => ['admission', 'fee', 'attendance'].includes(a.type));
  }, [activities, students]);

  if (hasNoAssignment) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '70vh',
          px: 2,
          py: 4,
          position: 'relative',
        }}
      >
        {/* Soft Background Radial Glow */}
        <Box
          sx={{
            position: 'absolute',
            width: 360,
            height: 360,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(79, 70, 229, 0.1) 0%, rgba(59, 130, 246, 0.04) 50%, rgba(255,255,255,0) 70%)',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />

        <Paper
          elevation={0}
          sx={{
            position: 'relative',
            zIndex: 1,
            p: { xs: 4, sm: 5 },
            maxWidth: 540,
            width: '100%',
            borderRadius: tokens.radius.xl || '24px',
            border: `1px solid ${tokens.colors.primary[100]}`,
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            boxShadow: '0 20px 40px -15px rgba(30, 58, 138, 0.08), 0 0 1px 1px rgba(59, 130, 246, 0.1)',
            overflow: 'hidden',
          }}
        >
          {/* Status Chip */}
          <Chip
            icon={<Sparkles size={14} style={{ color: tokens.colors.primary.main }} />}
            label="ACCOUNT SETUP PENDING"
            size="small"
            sx={{
              mb: 3,
              px: 1,
              py: 0.5,
              fontWeight: 700,
              fontSize: '0.72rem',
              letterSpacing: 0.8,
              backgroundColor: tokens.colors.primary[50],
              color: tokens.colors.primary.main,
              border: `1px solid ${tokens.colors.primary[100]}`,
              borderRadius: tokens.radius.full,
            }}
          />

          {/* Dual Ring Avatar Icon */}
          <Box
            sx={{
              position: 'relative',
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${tokens.colors.primary[50]} 0%, #EFF6FF 100%)`,
                border: `2px solid ${tokens.colors.primary[100]}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 16px -4px rgba(59, 130, 246, 0.15)',
              }}
            >
              <Building2 size={42} color={tokens.colors.primary.main} />
            </Box>
            <Box
              sx={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                backgroundColor: tokens.colors.warning.main,
                color: '#ffffff',
                borderRadius: '50%',
                p: 0.6,
                display: 'flex',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              }}
            >
              <ShieldAlert size={18} />
            </Box>
          </Box>

          {/* Title & Message */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 800,
              color: tokens.colors.text.primary,
              mb: 1,
              fontSize: { xs: '1.35rem', sm: '1.5rem' },
              letterSpacing: -0.3,
            }}
          >
            No Assignment Found
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: tokens.colors.text.secondary,
              fontSize: '0.95rem',
              lineHeight: 1.6,
              mb: 3.5,
              maxWidth: 420,
            }}
          >
            there is no assignment to you yet
            <br />
            please contact ShreeJi Admin
          </Typography>

          {/* Guidance Info Card */}
          <Box
            sx={{
              width: '100%',
              p: 2,
              borderRadius: tokens.radius.md,
              backgroundColor: tokens.colors.grey[50],
              border: `1px solid ${tokens.colors.grey[200]}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              textAlign: 'left',
            }}
          >
            <Box
              sx={{
                p: 1,
                borderRadius: tokens.radius.sm,
                backgroundColor: tokens.colors.primary[50],
                color: tokens.colors.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <HelpCircle size={20} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
              Need assistance? Ask your ShreeJi Administrator to assign a school or grant permissions to your account.
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  const isOrgAdmin = normalizedRole === 'ORGANIZATION_ADMIN' || normalizedRole === 'ORG_ADMIN';
  const hasDedicatedDashboard =
    isSuperAdmin ||
    isOrgAdmin ||
    ['PRINCIPAL', 'RECEPTION', 'RECEPTIONIST', 'ACCOUNTANT', 'TEACHER', ROLES.PRINCIPAL, ROLES.RECEPTION, ROLES.ACCOUNTANT, ROLES.TEACHER].includes(normalizedRole);

  // For roles without a dashboard view, redirect to their primary feature module
  if (!hasDedicatedDashboard) {
    const navItems = buildDynamicNavigation(activeMods, user?.role);
    const firstNavPath =
      navItems[0]?.children?.[0]?.path ||
      (navItems[0]?.path && navItems[0].path !== '#' ? navItems[0].path : '/');
    return <Navigate to={firstNavPath} replace />;
  }

  if (loading) return <Loader message="Loading dashboard data..." />;
  if (error) return <ErrorState message={error} onRetry={() => window.location.reload()} />;

  // Ensure we have data before rendering role dashboards
  if (!combinedStats || !combinedActivities || !combinedCharts) return null;

  const renderDashboard = () => {
    switch (normalizedRole) {
      case 'ORGANIZATION_ADMIN':
      case ROLES.ORGANIZATION_ADMIN:
        return <OwnerDashboard stats={combinedStats} charts={combinedCharts} activities={combinedActivities} />;
      case 'PRINCIPAL':
      case ROLES.PRINCIPAL:
        return <PrincipalDashboard stats={combinedStats} charts={combinedCharts} activities={combinedActivities} />;
      case 'RECEPTION':
      case 'RECEPTIONIST':
      case ROLES.RECEPTION:
        return <ReceptionDashboard stats={combinedStats} activities={combinedActivities} />;
      case 'ACCOUNTANT':
      case ROLES.ACCOUNTANT:
        return <AccountantDashboard stats={combinedStats} charts={combinedCharts} activities={combinedActivities} />;
      case 'TEACHER':
      case ROLES.TEACHER:
        return <TeacherDashboard />;
      default:
        // Default to OwnerDashboard for organization admins / staff
        return <OwnerDashboard stats={combinedStats} charts={combinedCharts} activities={combinedActivities} />;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {renderDashboard()}
    </Box>
  );
}
